import { extent, max, line } from 'd3';
import Axis from '../Base/axis';
import Data from '../Base/data';
import Dims from '../Base/dims';
import Margin from '../Base/margin';
import Svg from '../Base/svg';
import Transforms from '../Base/transforms';
import ToolTip from '../ToolTip';
import { ChartCtrParams } from '../types';
import Utils from '../Utils';

export default class LineChart {
  private elementId: string;
  private element: Svg;

  private dims: Dims;
  private margin: Margin;

  private toolTip: ToolTip;
  private useToolTip = false;
  private animators: any;

  public data: Data;
  private _xField = 'x';
  private _yField = 'y';

  public xAxis: Axis;
  public yAxis: Axis;

  public transforms: Transforms;

  /**
   * Constructs a new LineChart instance
   * ```ts
   * const data = [{ x: 1, y: 135.9 }, ... ];
   * const line = new AchoViz.LineChart({ elementId: '#line', data });
   * ```
   * @param params - constructor params
   * @returns instance of a LineChart
   */
  constructor(params: ChartCtrParams = {}) {
    this.elementId = params.elementId;
    this.margin = new Margin(params.margin);

    this.data = new Data(params.data);
    this.dims = new Dims(params.height, params.width);

    this._xField = params.xField ?? 'x';
    this._yField = params.yField ?? 'y';

    this.transforms = new Transforms({ [this._xField]: params.xTransform, [this._yField]: params.yTransform });

    this.yAxis = new Axis({ loc: 'left' });
    this.xAxis = new Axis({ loc: 'bottom' });
    this._useAutoAxis();
  }

  /**
   * Handles drawing, mounting, and rendering the line chart instance
   */
  public render() {
    const { elementId, margin } = this;
    const { height, width } = this.dims.both();

    this.element = new Svg({ elementId, height, width, margin });
    this.element.draw();

    this._drawXAxis();
    this._drawYAxis();
    this._drawLine();
  }

  /**
   * Draws the x axis
   */
  private _drawXAxis() {
    const { d3Svg } = this.element;
    const { height: h } = this.dims.marDims(this.margin);

    const translate = `translate(0, ${h})`;
    const ax = Utils.drawAxis(d3Svg, this.xAxis, { translate });
  }

  /**
   * Draws the y axis
   */
  private _drawYAxis() {
    const { d3Svg } = this.element;
    const ax = Utils.drawAxis(d3Svg, this.yAxis);
  }

  /**
   * Draws the line with respect to the data provided to the instance
   */
  private _drawLine() {
    const { xAxis, _xField, _yField, yAxis } = this;
    const _line = line()
      .x((d) => xAxis.scale(d[_xField]))
      .y((d) => yAxis.scale(d[_yField]));

    const data = this.data.consume();

    const attr = {
      fill: 'none',
      stroke: 'steelblue',
      'stroke-width': 1.5,
      d: _line(data),
    };

    Utils.drawLine(this.element.d3Svg, data, { attr });
  }

  /**
   * Automatically configures the axis scales, domain, and ranges
   */
  private _useAutoAxis() {
    const { _xField, _yField } = this;

    const { height: h, width: w } = this.dims.marDims(this.margin);
    const data = this.data.consume();

    const xDomain = extent(data, (d) => d[_xField]);
    const xRange = [0, w];
    const xScaleFn = this.transforms.getScale(this._xField);

    const xScale = xScaleFn().domain(xDomain).range(xRange);
    this.xAxis.setDomain(xDomain);
    this.xAxis.setScale(xScale);

    const yMax = max(data, (d) => d[_yField]);
    const yRange = [h, 0];
    const yDomain = [0, yMax];
    const yScaleFn = this.transforms.getScale(this._yField);

    const yScale = yScaleFn().domain(yDomain).range(yRange);
    this.yAxis.setDomain(yDomain);
    this.yAxis.setScale(yScale);
  }
}
